{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "title": "PainterView",
  "description": "Pinhole camera from which the 3D reconstruction best lines up with Cornelis Anthonisz.'s 1538 painting, fitted to hand-identified correspondences (src/data/painting.correspondences.json). Layout frame: x east, y north, z up, metres, origin 52.3731 N 4.8926 E. three.js frame: X east, Y up, Z south, i.e. (X, Y, Z) = (x, z, -y). Pixels: 2000 x 1454 frame, x right, y down.",
  "type": "object",
  "additionalProperties": false,
  "required": [
    "version",
    "image",
    "layout",
    "three",
    "fovYDeg",
    "aspect",
    "yawDeg",
    "pitchDeg",
    "rollDeg",
    "focalPx",
    "principalPoint",
    "rmsPx",
    "inlierRmsPx",
    "robustLoss",
    "residuals",
    "conventions",
    "diagnostics",
    "residualWarp"
  ],
  "properties": {
    "version": {
      "description": "Schema version.",
      "const": 1
    },
    "image": {
      "$ref": "#/$defs/Image"
    },
    "layout": {
      "title": "LayoutCamera",
      "description": "Camera in the layout frame (metres).",
      "type": "object",
      "additionalProperties": false,
      "required": [
        "position",
        "target"
      ],
      "properties": {
        "position": {
          "$ref": "#/$defs/Vec3",
          "description": "Eye position [x east, y north, z up], metres."
        },
        "target": {
          "$ref": "#/$defs/Vec3",
          "description": "Where the optical axis meets the ground plane z = 0, metres."
        }
      }
    },
    "three": {
      "title": "ThreeCamera",
      "description": "The same camera in the three.js world frame, for camera.position / camera.lookAt(target) with camera.up = [0, 1, 0].",
      "type": "object",
      "additionalProperties": false,
      "required": [
        "position",
        "target",
        "up"
      ],
      "properties": {
        "position": {
          "$ref": "#/$defs/Vec3",
          "description": "Eye position [X east, Y up, Z south], metres."
        },
        "target": {
          "$ref": "#/$defs/Vec3",
          "description": "Look-at point [X, Y, Z], metres."
        },
        "up": {
          "$ref": "#/$defs/Vec3",
          "description": "Up vector; always [0, 1, 0]."
        }
      }
    },
    "fovYDeg": {
      "type": "number",
      "description": "Vertical field of view, degrees (three.js PerspectiveCamera.fov)."
    },
    "aspect": {
      "type": "number",
      "description": "Image width / height of the painting frame."
    },
    "yawDeg": {
      "type": "number",
      "description": "Compass bearing of the view direction, degrees clockwise from north."
    },
    "pitchDeg": {
      "type": "number",
      "description": "Elevation of the view direction, degrees; negative looks down."
    },
    "rollDeg": {
      "type": "number",
      "description": "Rotation about the view axis, degrees; positive turns the image content counter-clockwise. Apply after lookAt with camera.rotateZ(-rollDeg in radians)."
    },
    "focalPx": {
      "type": "number",
      "description": "Focal length in pixels of the 2000 x 1454 frame."
    },
    "principalPoint": {
      "$ref": "#/$defs/Vec2",
      "description": "Principal point [x, y] in pixels (image centre in the primary fit)."
    },
    "rmsPx": {
      "type": "number",
      "description": "Root-mean-square reprojection error of all fitted points, pixels (2000 px frame)."
    },
    "inlierRmsPx": {
      "type": "number",
      "description": "RMS reprojection error without flagged outliers, pixels."
    },
    "robustLoss": {
      "title": "RobustLoss",
      "type": "object",
      "additionalProperties": false,
      "required": [
        "type",
        "fScalePx",
        "weights"
      ],
      "properties": {
        "type": {
          "type": "string",
          "description": "scipy.optimize.least_squares loss."
        },
        "fScalePx": {
          "type": "number",
          "description": "Soft-L1 transition scale, pixels (median residual of the L2 pre-fit)."
        },
        "weights": {
          "type": "string",
          "description": "How correspondence confidences weight the residuals."
        }
      }
    },
    "residuals": {
      "type": "array",
      "items": {
        "$ref": "#/$defs/Residual"
      },
      "description": "Per fitted point: projected minus painted pixel."
    },
    "conventions": {
      "type": "string",
      "description": "Frames and angle conventions in words."
    },
    "diagnostics": {
      "$ref": "#/$defs/Diagnostics"
    },
    "residualWarp": {
      "$ref": "#/$defs/ResidualWarp"
    }
  },
  "$defs": {
    "Vec2": {
      "type": "array",
      "items": {
        "type": "number"
      },
      "minItems": 2,
      "maxItems": 2
    },
    "Vec3": {
      "type": "array",
      "items": {
        "type": "number"
      },
      "minItems": 3,
      "maxItems": 3
    },
    "Image": {
      "title": "PaintingImage",
      "type": "object",
      "additionalProperties": false,
      "required": [
        "file",
        "width",
        "height"
      ],
      "properties": {
        "file": {
          "type": "string",
          "description": "Project-relative path of the painting image."
        },
        "width": {
          "type": "integer",
          "description": "Pixels."
        },
        "height": {
          "type": "integer",
          "description": "Pixels."
        }
      }
    },
    "Residual": {
      "title": "Residual",
      "type": "object",
      "additionalProperties": false,
      "required": [
        "id",
        "px",
        "dx",
        "dy",
        "outlier"
      ],
      "properties": {
        "id": {
          "type": "string",
          "description": "Correspondence id."
        },
        "px": {
          "type": "number",
          "description": "Residual length, pixels."
        },
        "dx": {
          "type": "number",
          "description": "Projected minus painted x, pixels."
        },
        "dy": {
          "type": "number",
          "description": "Projected minus painted y, pixels."
        },
        "outlier": {
          "type": "boolean",
          "description": "Residual above 4 robust standard deviations."
        }
      }
    },
    "CameraSummary": {
      "title": "CameraSummary",
      "type": "object",
      "additionalProperties": false,
      "required": [
        "position",
        "yawDeg",
        "pitchDeg",
        "rollDeg",
        "fovYDeg",
        "principalPoint",
        "rmsPx",
        "inlierRmsPx"
      ],
      "properties": {
        "position": {
          "$ref": "#/$defs/Vec3",
          "description": "Layout position, metres."
        },
        "yawDeg": {
          "type": "number",
          "description": "Degrees."
        },
        "pitchDeg": {
          "type": "number",
          "description": "Degrees."
        },
        "rollDeg": {
          "type": "number",
          "description": "Degrees."
        },
        "fovYDeg": {
          "type": "number",
          "description": "Degrees."
        },
        "principalPoint": {
          "$ref": "#/$defs/Vec2",
          "description": "Pixels."
        },
        "rmsPx": {
          "type": "number",
          "description": "Pixels."
        },
        "inlierRmsPx": {
          "type": "number",
          "description": "Pixels."
        }
      }
    },
    "LadderRow": {
      "title": "ModelLadderRow",
      "type": "object",
      "additionalProperties": false,
      "required": [
        "model",
        "parameters",
        "rmsPx",
        "medianPx"
      ],
      "properties": {
        "model": {
          "type": "string"
        },
        "parameters": {
          "type": "integer",
          "description": "Free parameters of the model."
        },
        "rmsPx": {
          "type": "number",
          "description": "Pixels."
        },
        "medianPx": {
          "type": "number",
          "description": "Median residual length, pixels."
        }
      }
    },
    "HeightRow": {
      "title": "VerticalExaggerationRow",
      "type": "object",
      "additionalProperties": false,
      "required": [
        "id",
        "heightM",
        "paintedHeightPx",
        "predictedHeightPx",
        "ratio"
      ],
      "properties": {
        "id": {
          "type": "string"
        },
        "heightM": {
          "type": "number",
          "description": "Sourced height above the base point, metres."
        },
        "paintedHeightPx": {
          "type": "number",
          "description": "Pixels."
        },
        "predictedHeightPx": {
          "type": "number",
          "description": "Pixels, by the fitted camera."
        },
        "ratio": {
          "type": "number",
          "description": "Painted / predicted apparent height."
        }
      }
    },
    "LooError": {
      "title": "LeaveOneOutError",
      "type": "object",
      "additionalProperties": false,
      "required": [
        "id",
        "px"
      ],
      "properties": {
        "id": {
          "type": "string"
        },
        "px": {
          "type": "number",
          "description": "Distance between the painted pixel and its prediction by a camera fitted without it, pixels."
        }
      }
    },
    "Diagnostics": {
      "title": "PainterViewDiagnostics",
      "type": "object",
      "additionalProperties": false,
      "required": [
        "rollFree",
        "freePrincipalPoint",
        "modelLadder",
        "horizon",
        "verticalExaggeration",
        "leaveOneOut",
        "unfitted"
      ],
      "properties": {
        "rollFree": {
          "$ref": "#/$defs/CameraSummary",
          "description": "Fit with roll as a free parameter."
        },
        "freePrincipalPoint": {
          "$ref": "#/$defs/CameraSummary",
          "description": "Fit with the principal point free (bounded to the image)."
        },
        "modelLadder": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/LadderRow"
          }
        },
        "horizon": {
          "title": "HorizonDiagnostics",
          "type": "object",
          "additionalProperties": false,
          "required": [
            "paintedRowPx",
            "paintedTiltDeg",
            "columnsUsed",
            "cameraRowPx"
          ],
          "properties": {
            "paintedRowPx": {
              "type": "number",
              "description": "Measured painted horizon row at the image centre, pixels."
            },
            "paintedTiltDeg": {
              "type": "number",
              "description": "Measured tilt of the painted horizon, degrees."
            },
            "columnsUsed": {
              "type": "integer",
              "description": "Image columns in the horizon line fit."
            },
            "cameraRowPx": {
              "type": "number",
              "description": "Row of the fitted camera's true horizon at the image centre, pixels (negative: above the image)."
            }
          }
        },
        "verticalExaggeration": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/HeightRow"
          }
        },
        "leaveOneOut": {
          "title": "LeaveOneOut",
          "type": "object",
          "additionalProperties": false,
          "required": [
            "positionRangeM",
            "yawRangeDeg",
            "pitchRangeDeg",
            "fovYRangeDeg",
            "predictionErrors"
          ],
          "properties": {
            "positionRangeM": {
              "$ref": "#/$defs/Vec3",
              "description": "Max minus min of x, y, z over the refits, metres."
            },
            "yawRangeDeg": {
              "type": "number",
              "description": "Degrees."
            },
            "pitchRangeDeg": {
              "type": "number",
              "description": "Degrees."
            },
            "fovYRangeDeg": {
              "type": "number",
              "description": "Degrees."
            },
            "predictionErrors": {
              "type": "array",
              "items": {
                "$ref": "#/$defs/LooError"
              }
            }
          }
        },
        "unfitted": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/Residual"
          },
          "description": "Residuals of correspondences excluded from the fit (feature tops)."
        }
      }
    },
    "ResidualWarp": {
      "title": "ResidualWarp",
      "description": "What the painter projection (pinhole, forward compression, tower exaggeration) leaves of the painter's layout: the shift, in pixels of the 2000 x 1454 frame, from where a point renders to where the painter drew it, as a function of the rendered pixel (u, v). Along rows: D = affine[0] + affine[1] u / s + affine[2] v / s + sum_i weights[i] r_i^2 ln r_i, with r_i = |(u, v) - centresPx[i]| / s and s = scalePx. Down columns only where `vertical` holds a column shift, which keeps the painted horizon row 145.1 in place; without it rows do not move. Written by tools/painterWarp (node tools/painterWarp/run.mjs) from this file's camera, src/data/painting.correspondences.json and the lines of tools/painterWarp/evidence.json, whose points slide along the painted line; re-run it whenever any of them or the layout lines the evidence names change.",
      "type": "object",
      "additionalProperties": false,
      "required": [
        "method",
        "scalePx",
        "smoothing",
        "centresPx",
        "weights",
        "affine",
        "excluded",
        "looRmsPx",
        "looRmsNoWarpPx",
        "fitRmsPx",
        "canvasPx",
        "minJacobianFrame",
        "minJacobianCanvas",
        "maxShiftFramePx",
        "maxShiftCanvasPx",
        "maxCurvaturePerPx",
        "points",
        "slidingPoints",
        "candidates"
      ],
      "properties": {
        "method": {
          "type": "string",
          "description": "Model, weights and selection rule in words."
        },
        "scalePx": {
          "type": "number",
          "exclusiveMinimum": 0,
          "description": "Spline length unit s, pixels."
        },
        "smoothing": {
          "type": "number",
          "minimum": 0,
          "description": "Smoothing of the chosen thin-plate spline: lambda in (K + lambda W^-1) w + P a = y, P^T w = 0, with K_ij = r_ij^2 ln r_ij (r in units of s), P_i = [1, u_i / s, v_i / s] and W = diag(confidence)."
        },
        "centresPx": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/Vec2"
          },
          "minItems": 1,
          "description": "Spline centres: the rendered pixels of the fitted correspondences, [u, v]."
        },
        "weights": {
          "type": "array",
          "items": {
            "type": "number"
          },
          "minItems": 1,
          "description": "Radial weight of each centre, pixels; as many as centresPx."
        },
        "affine": {
          "$ref": "#/$defs/Vec3",
          "description": "Affine part [a0 px, a1 px per s along u, a2 px per s along v]."
        },
        "vertical": {
          "$ref": "#/$defs/VerticalWarp",
          "description": "The column shift, where the vertical residuals predict each other; absent, rows do not move."
        },
        "excluded": {
          "type": "array",
          "items": {
            "type": "string"
          },
          "description": "Fitted ground correspondences left out as the camera fit's outliers."
        },
        "looRmsPx": {
          "type": "number",
          "minimum": 0,
          "description": "Leave-one-out prediction RMS of the chosen warp over the fitted points, pixels."
        },
        "looRmsNoWarpPx": {
          "type": "number",
          "minimum": 0,
          "description": "The same points without the warp: the inlier RMS of the projection, pixels."
        },
        "fitRmsPx": {
          "type": "number",
          "minimum": 0,
          "description": "In-sample RMS of painted minus warped rendered pixels, pixels."
        },
        "canvasPx": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/Vec2"
          },
          "minItems": 2,
          "maxItems": 2,
          "description": "Corners [[u, v] min, [u, v] max] of the frame region some canvas shows at the painter view, from 21:9 to 9:16, pixels."
        },
        "minJacobianFrame": {
          "type": "number",
          "description": "Smallest Jacobian determinant of the warp over the 2000 x 1454 frame: (1 + dD/du)(1 + dDy/dv) - (dD/dv)(dDy/du) with the column shift Dy, which is 1 + dD/du without one."
        },
        "minJacobianCanvas": {
          "type": "number",
          "description": "Smallest Jacobian determinant over canvasPx."
        },
        "maxShiftFramePx": {
          "type": "number",
          "minimum": 0,
          "description": "Largest |D| over the frame, pixels."
        },
        "maxShiftCanvasPx": {
          "type": "number",
          "minimum": 0,
          "description": "Largest |D| over canvasPx, pixels."
        },
        "maxCurvaturePerPx": {
          "type": "number",
          "minimum": 0,
          "description": "Largest second derivative of D in any direction over the frame, 1/pixel: a straight edge h pixels long deviates at most h^2 x this / 8 from the warped curve."
        },
        "points": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/WarpPoint"
          },
          "description": "Per fitted correspondence."
        },
        "slidingPoints": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/WarpPoint"
          },
          "description": "The points of the sliding lines of tools/painterWarp/evidence.json, fitted with the correspondences: each paintedPx is where the point settled on its painted line, and the leave-one-out error holds it out there."
        },
        "candidates": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/WarpCandidate"
          },
          "description": "Every model scored, per component."
        }
      }
    },
    "VerticalWarp": {
      "title": "VerticalWarp",
      "description": "The shift down columns, in pixels of the frame, from where a point renders to where the painter drew it: Dy = (w - heldRowPx) / s h(u, w), w = min(v, lastFittedRowPx), with h = affine[0] + affine[1] u / s + affine[2] w / s + sum_i weights[i] r_i^2 ln r_i, r_i = |(u, w) - centresPx[i]| / s and s the scalePx of the row shift, so the painted horizon row does not move and below the lowest fitted point each column keeps the shift it has on that point's row.",
      "type": "object",
      "additionalProperties": false,
      "required": [
        "heldRowPx",
        "lastFittedRowPx",
        "smoothing",
        "centresPx",
        "weights",
        "affine",
        "minColumnSlopeFrame",
        "minColumnSlopeCanvas",
        "maxShiftFramePx",
        "maxShiftCanvasPx",
        "maxCurvaturePerPx"
      ],
      "properties": {
        "heldRowPx": {
          "type": "number",
          "description": "The painted horizon row, where the column shift vanishes, pixels."
        },
        "lastFittedRowPx": {
          "type": "number",
          "description": "The lowest rendered row of a fitted correspondence, the largest v of centresPx: below it no painted point fixes the rows, so Dy keeps its value on this row, pixels."
        },
        "smoothing": {
          "type": "number",
          "minimum": 0,
          "description": "Smoothing of the chosen thin-plate spline h, as for the row shift, with each confidence times ((v - heldRowPx) / s)^2 in W, which charges the misfit of Dy itself."
        },
        "centresPx": {
          "type": "array",
          "items": {
            "$ref": "#/$defs/Vec2"
          },
          "minItems": 1,
          "description": "Spline centres: the rendered pixels of the fitted correspondences, [u, v]."
        },
        "weights": {
          "type": "array",
          "items": {
            "type": "number"
          },
          "minItems": 1,
          "description": "Radial weight of each centre, pixels; as many as centresPx."
        },
        "affine": {
          "$ref": "#/$defs/Vec3",
          "description": "Affine part of h [a0 px, a1 px per s along u, a2 px per s along v]."
        },
        "minColumnSlopeFrame": {
          "type": "number",
          "description": "Smallest column slope 1 + dDy/dv over the 2000 x 1454 frame: rows keep their order where it is positive."
        },
        "minColumnSlopeCanvas": {
          "type": "number",
          "description": "Smallest column slope over canvasPx."
        },
        "maxShiftFramePx": {
          "type": "number",
          "minimum": 0,
          "description": "Largest |Dy| over the frame, pixels."
        },
        "maxShiftCanvasPx": {
          "type": "number",
          "minimum": 0,
          "description": "Largest |Dy| over canvasPx, pixels."
        },
        "maxCurvaturePerPx": {
          "type": "number",
          "minimum": 0,
          "description": "Largest second derivative of Dy in any direction over the frame, off the last fitted row, where its slope down the columns stops, 1/pixel."
        }
      }
    },
    "WarpPoint": {
      "title": "WarpPoint",
      "type": "object",
      "additionalProperties": false,
      "required": [
        "id",
        "renderedPx",
        "paintedPx",
        "confidence",
        "residualPx",
        "warpedResidualPx",
        "looPx",
        "leverage"
      ],
      "properties": {
        "id": {
          "type": "string",
          "description": "Correspondence id, or <line>-<k> for the k-th point of a sliding line."
        },
        "line": {
          "type": "string",
          "description": "The sliding line of tools/painterWarp/evidence.json the point lies on; its paintedPx is where it settled on the painted line."
        },
        "renderedPx": {
          "$ref": "#/$defs/Vec2",
          "description": "Where the painter projection renders the point, [u, v] pixels."
        },
        "paintedPx": {
          "$ref": "#/$defs/Vec2",
          "description": "Where the painter drew it, [u, v] pixels."
        },
        "confidence": {
          "type": "number",
          "description": "Correspondence confidence, the weight of its misfit."
        },
        "residualPx": {
          "type": "number",
          "minimum": 0,
          "description": "Painted minus rendered, length, pixels."
        },
        "warpedResidualPx": {
          "type": "number",
          "minimum": 0,
          "description": "Painted minus warped rendered, length, pixels."
        },
        "looPx": {
          "type": "number",
          "minimum": 0,
          "description": "Prediction error with the point left out of the fit, pixels."
        },
        "leverage": {
          "type": "number",
          "description": "The point's own share of the row shift fitted at it (hat matrix diagonal)."
        }
      }
    },
    "WarpCandidate": {
      "title": "WarpCandidate",
      "type": "object",
      "additionalProperties": false,
      "required": [
        "component",
        "model",
        "parameter",
        "looRmsPx",
        "effectiveParameters",
        "chosen"
      ],
      "properties": {
        "component": {
          "type": "string",
          "enum": [
            "horizontal",
            "vertical"
          ],
          "description": "Which shift the model predicts; a vertical one vanishes on the painted horizon row."
        },
        "model": {
          "type": "string",
          "enum": [
            "none",
            "polynomial",
            "thinPlate"
          ],
          "description": "No warp, a weighted polynomial, or a smoothing thin-plate spline."
        },
        "parameter": {
          "type": "number",
          "description": "Polynomial degree, thin-plate smoothing lambda, or 0 for none."
        },
        "looRmsPx": {
          "type": "number",
          "minimum": 0,
          "description": "Leave-one-out RMS of this component, pixels."
        },
        "effectiveParameters": {
          "type": "number",
          "description": "Trace of the hat matrix."
        },
        "chosen": {
          "type": "boolean",
          "description": "Picked by the one-standard-error rule."
        }
      }
    }
  }
}
